/* eslint-disable react/prop-types */

import React, { Component } from "react";
import PropTypes from "prop-types";
import Typography from "material-ui/Typography";
import Input from "material-ui/Input";
import List, { ListItemText, ListItem } from "material-ui/List";
import { MenuItem } from "material-ui/Menu";
import { FormControlLabel, Radio, RadioGroup } from "material-ui";
import Checkbox from "material-ui/Checkbox";
import ArrowDropDownIcon from "material-ui-icons/ArrowDropDown";
import ArrowDropUpIcon from "material-ui-icons/ArrowDropUp";
import ClearIcon from "material-ui-icons/Clear";
import Select from "react-select";
import "react-select/dist/react-select.css";
import { availableLanguages } from "../constants/languages.constants";
import { withStylesAndTranslation } from "../utils/container";
import { LanguageSettings } from "../records/userSettings.record";
import { List as ImmutableList } from "immutable";

const suggestions = t => {
    let langs = availableLanguages.map(language => ({
        value: language,
        label: t("languages.codes." + language),
        clearableValue: false
    }));
    langs.sort((a, b) => {
        if (a.label < b.label) {
            return -1;
        } else if (a.label > b.label) {
            return 1;
        }
        return 0;
    });
    return langs;
};

const notIsLearningValues = [
    {
        value: "native",
        label: "languageSelect.I'm a native speaker"
    },
    {
        value: "fluent",
        label: "languageSelect.I'm fluent, but not a native speaker"
    }
];

const isLearningValues = [
    {
        value: "beginner",
        label: "languageSelect.I'm at a beginner's level"
    },
    {
        value: "intermediate",
        label: "languageSelect.I'm at an intermediate level"
    },
    {
        value: "advanced",
        label: "languageSelect.I'm at an advanced level"
    }
];

const getLabels = isLearning =>
    isLearning ? isLearningValues : notIsLearningValues;

class Option extends Component {
    handleClick = event => {
        this.props.onSelect(this.props.option, event);
    };

    render() {
        const { children, isFocused, isSelected, onFocus } = this.props;

        return (
            <MenuItem
                onFocus={onFocus}
                selected={isFocused}
                onClick={this.handleClick}
                component="div"
                style={{
                    fontWeight: isSelected ? 500 : 400
                }}>
                {children}
            </MenuItem>
        );
    }
}

class SelectWrapped extends Component {
    render() {
        const { ...other } = this.props;

        return (
            <Select
                optionComponent={Option}
                noResultsText={
                    <Typography>
                        {"No results found"}
                    </Typography>
                }
                arrowRenderer={arrowProps => {
                    return arrowProps.isOpen
                        ? <ArrowDropUpIcon />
                        : <ArrowDropDownIcon />;
                }}
                clearable={false}
                clearRenderer={() => <ClearIcon />}
                valueComponent={valueProps => {
                    const { children, onRemove } = valueProps;

                    if (onRemove) {
                        return <div />;
                    }

                    return (
                        <div className="Select-value">
                            {children}
                        </div>
                    );
                }}
                {...other}
            />
        );
    }
}

const ITEM_HEIGHT = 48;

const styleSheet = theme => ({
    group: {
        margin: `${theme.spacing.unit}px ${theme.spacing.unit * 8}px`
    },
    listItemText: {
        padding: `0 ${theme.spacing.unit * 2}px 0 ${theme.spacing.unit * 2}px`
    },
    listItemGutters: {
        paddingLeft: 0,
        paddingRight: 0
    },
    clickable: {
        cursor: "pointer"
    },
    root: {
        flexGrow: 1,
        width: 290,
        margin: `${theme.spacing.unit / 2}px 0 0 0`
    },
    inputSingleline: {
        height: "inherit"
    },
    // We had to use a lot of global selectors in order to style react-select.
    // We are waiting on https://github.com/JedWatson/react-select/issues/1679
    // to provide a better implementation.
    // Also, we had to reset the default style injected by the library.
    "@global": {
        ".Select-control": {
            display: "flex",
            alignItems: "center",
            border: 0,
            height: "auto",
            background: "transparent",
            "&:hover": {
                boxShadow: "none"
            }
        },
        ".Select-multi-value-wrapper": {
            flexGrow: 1,
            display: "flex",
            flexWrap: "wrap"
        },
        ".Select--multi .Select-input": {
            margin: 0
        },
        ".Select.has-value.is-clearable.Select--single > .Select-control .Select-value": {
            padding: 0
        },
        ".Select-noresults": {
            padding: theme.spacing.unit * 2
        },
        ".Select-input": {
            display: "inline-flex !important",
            padding: 0,
            height: "auto"
        },
        ".Select-input input": {
            background: "transparent",
            border: 0,
            padding: 0,
            cursor: "default",
            display: "inline-block",
            fontFamily: "inherit",
            fontSize: "inherit",
            margin: 0,
            outline: 0
        },
        ".Select-placeholder, .Select--single .Select-value": {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.pxToRem(16),
            padding: 0
        },
        ".Select-placeholder": {
            opacity: 0.42,
            color: theme.palette.common.black
        },
        ".Select-menu-outer": {
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[2],
            position: "absolute",
            left: 0,
            top: `calc(100% + ${theme.spacing.unit}px)`,
            width: "100%",
            zIndex: 2,
            maxHeight: ITEM_HEIGHT * 4.5
        },
        ".Select.is-focused:not(.is-open) > .Select-control": {
            boxShadow: "none"
        },
        ".Select-menu": {
            maxHeight: ITEM_HEIGHT * 4.5,
            overflowY: "auto"
        },
        ".Select-menu div": {
            boxSizing: "content-box"
        },
        ".Select-arrow-zone, .Select-clear-zone": {
            color: theme.palette.action.active,
            cursor: "pointer",
            height: 21,
            width: 21,
            zIndex: 1
        },
        // Only for screen readers. We can't use display none.
        ".Select-aria-only": {
            position: "absolute",
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            height: 1,
            width: 1,
            margin: -1
        }
    }
});

class LanguageMultiSelect extends React.Component {
    handleChangeMulti = multi => {
        const { onChange, selectedLanguages, isLearning } = this.props;
        const immMulti = ImmutableList(multi);
        const newSelectedLanguages = immMulti
            .map(({ value }) => {
                let existing = selectedLanguages.find(
                    sl => sl.language === value && sl.isLearning === isLearning
                );
                if (existing) {
                    return existing;
                }

                return new LanguageSettings({
                    language: value,
                    isLearning: isLearning,
                    level: isLearning ? "beginner" : "native"
                });
            })
            .concat(
                selectedLanguages.filter(
                    sl => !immMulti.find(v => v.value === sl.language)
                )
            );
        onChange(newSelectedLanguages);
    };

    render() {
        const { classes, t, selectedLanguages } = this.props;
        let options = suggestions(t || (x => x));

        options = options.filter(language => {
            return !selectedLanguages.find(sl => {
                return language.value === sl.language;
            });
        });

        return (
            <div>
                <div className={classes.root}>
                    <Input
                        fullWidth
                        classes={{
                            root: classes.root,
                            inputSingleline: classes.inputSingleline
                        }}
                        inputComponent={SelectWrapped}
                        inputProps={{
                            classes,
                            value: [],
                            multi: true,
                            onChange: this.handleChangeMulti,
                            placeholder: t("languageSelect.Select a language"),
                            instanceId: "react-select-chip",
                            id: "react-select-chip",
                            name: "react-select-chip",
                            simpleValue: false,
                            options
                        }}
                    />
                </div>
                {this.renderSelected()}
            </div>
        );
    }

    handleRadioChange = language => {
        return (event, value) => {
            const { selectedLanguages, onChange } = this.props;
            const newLanguages = selectedLanguages.map(sl => {
                return language === sl.language
                    ? sl.merge({ level: value })
                    : sl;
            });
            if (onChange) {
                onChange(newLanguages);
            }
        };
    };

    handleLanguageChecked(language) {
        return () => {
            const { onChange, selectedLanguages } = this.props;
            onChange(selectedLanguages.filter(sl => sl.language !== language));
        };
    }

    renderSelected() {
        const { selectedLanguages, classes, isLearning, t } = this.props;

        let selected = selectedLanguages.filter(
            sl => sl.isLearning === isLearning
        );
        let disableRest = selected.count() === availableLanguages.length - 1;
        return (
            <List>
                {selected.map(selectedLanguage => {
                    const labels = getLabels(isLearning);
                    const language = selectedLanguage.language;
                    let radioElement = (
                        <RadioGroup
                            aria-label="level"
                            name="level"
                            className={classes.group}
                            value={selectedLanguage.level}
                            onChange={this.handleRadioChange(language)}>
                            {labels.map(({ value, label }) => {
                                return (
                                    <FormControlLabel
                                        key={value}
                                        value={value}
                                        control={<Radio />}
                                        label={
                                            <Typography type="body1">
                                                {t(label)}
                                            </Typography>
                                        }
                                    />
                                );
                            })}
                        </RadioGroup>
                    );
                    return (
                        <div key={language}>
                            <ListItem
                                classes={{ gutters: classes.listItemGutters }}>
                                <Checkbox
                                    checkedIcon={
                                        <ClearIcon style={{ fill: "#888" }} />
                                    }
                                    onChange={this.handleLanguageChecked(
                                        language
                                    )}
                                    value={language}
                                    disabled={disableRest}
                                    checked={true}
                                />
                                <ListItemText
                                    classes={{ root: classes.listItemText }}
                                    onClick={this.handleLanguageChecked(
                                        language
                                    )}
                                    primary={
                                        <div className={classes.clickable}>
                                            <Typography type="body1">
                                                {t(
                                                    "languages.codes." +
                                                        language
                                                )}
                                            </Typography>
                                        </div>
                                    }
                                />
                            </ListItem>
                            {radioElement}
                        </div>
                    );
                })}
            </List>
        );
    }
}

LanguageMultiSelect.propTypes = {
    classes: PropTypes.object.isRequired,
    selectedLanguages: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    isLearning: PropTypes.bool,
    t: PropTypes.func.isRequired
};

export default withStylesAndTranslation(LanguageMultiSelect, styleSheet);



// WEBPACK FOOTER //
// ./src/components/LanguageMultiSelect.jsx